import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SelectedPatientService } from '../services/selected-patient.service';
import { SearchStateService } from '../services/search-state.service';
import { Observable, of, throwError } from 'rxjs';
import { filter, switchMap, timeout, catchError, tap } from 'rxjs/operators';
import { PatientDetailsService } from '../services/patient-details.service';
import { PiamService } from '../services/piam.service';
import { PatientDetails, Payload, PatientModality, PatientFacility, PatientEmergencyContact, GroupedEmergencyContact } from '../models/patient-details.model';
import { PingOneModalComponent, PingOneModalData } from './pingone-modal/pingone-modal.component';
import { PingUser } from '../models/ping-user.model';

@Component({
  selector: 'app-patient-details',
  templateUrl: './patient-details.component.html',
  styleUrls: ['./patient-details.component.scss'],
  standalone: false
})
export class PatientDetailsComponent implements OnInit, OnDestroy {
  private selectedPatientService = inject(SelectedPatientService);
  private patientDetailsService = inject(PatientDetailsService);
  private piamService = inject(PiamService);
  private searchStateService = inject(SearchStateService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds timeout

  patientDetails$: Observable<PatientDetails | null> = of(null);
  errorMessage: string | null = null;
  isLoading = true;

  // PingOne user data - will be populated by PIAM service
  pingUsers: PingUser[] = [];
  pingUserLoading = false;
  pingUserError: string | null = null;

  ngOnInit() {
    this.patientDetails$ = this.selectedPatientService.selectedPatient$.pipe(
      filter(patient => !!patient),
      switchMap(patient => {
        if (patient && patient.masterPatientIdentifier) {
          this.isLoading = true;
          this.errorMessage = null;
          return this.patientDetailsService.getPatientDetails(patient.masterPatientIdentifier).pipe(
            timeout(this.REQUEST_TIMEOUT),
            catchError(err => {
              this.isLoading = false;

              if (err.name === 'TimeoutError') {
                this.errorMessage =
                  'Request timed out after 5 seconds. The backend may be experiencing issues.';
              } else if (err.status === 500) {
                const errorDetail =
                  err.error?.message ||
                  err.error?.error ||
                  'Internal server error';

                this.errorMessage =
                  `Backend error: ${errorDetail}. This patient may have missing data in some systems.`;
              } else if (err.status === 0) {
                this.errorMessage =
                  'Unable to connect to backend. Please check your network connection.';
              } else {
                this.errorMessage =
                  `Error loading patient details: ${
                    err.message || err.status || 'Unknown error'
                  }`;
              }

              console.error('Patient details error:', err);
              return of(null);
            }),
            tap(details => {
              this.isLoading = false;

              if (details?.payload) {
                this.fetchPingOneUser(details.payload);
              }
            })
          );
        }
        return of(null);
      })
    );

    /* // Subscribe to update loading state and fetch PingOne user
    this.patientDetails$.subscribe(details => {
      this.isLoading = false;

      // Fetch PingOne user when patient details are loaded
      if (details?.payload) {
        this.fetchPingOneUser(details.payload);
      }
    }); */
  }

  ngOnDestroy(): void {
    this.patientDetailsService.clearPatientDetailsCache();
  }

  /**
   * Fetch PingOne user data for the patient.
   * Returns all accounts matching the patient's MPI.
   */
  private fetchPingOneUser(payload: Payload): void {
    const firstName = payload.firstName;
    const lastName = payload.familyName;
    const dob = payload.birthDate;
    const mpi = payload.masterPatientIdentifier;

    if (!firstName || !lastName || !dob) {
      console.warn('Missing patient data for PingOne lookup');
      this.pingUsers = [];
      return;
    }

    this.pingUserLoading = true;
    this.pingUserError = null;

    this.piamService.searchUsersByPatient(firstName, lastName, dob, mpi).subscribe({
      next: (users) => {
        this.pingUserLoading = false;
        this.pingUsers = users;
      },
      error: (err) => {
        this.pingUserLoading = false;
        this.pingUserError = err.message || 'Failed to fetch PingOne users';
        console.error('PingOne lookup error:', err);
      }
    });
  }

  goBack(): void {
    this.patientDetailsService.clearPatientDetailsCache();
    // Navigate back to search with flag to restore previous state
    this.router.navigate(['/search'], { queryParams: { restore: 'true' } });
  }

  getInitials(payload: Payload): string {
    const first = payload.firstName?.charAt(0)?.toUpperCase() || '';
    const last = payload.familyName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}` || '?';
  }

  getActiveEmergencyContacts(payload: Payload): PatientEmergencyContact[] | null {
    return (payload?.patientEmergencyContacts && payload.patientEmergencyContacts != null) ?
      payload.patientEmergencyContacts.filter(em => {
        return (em.inactiveFlag === 0
          && this.isDefined(em.emergencyContactId)
          && this.isDefined(em.emergencyContactFirstName)
          && this.isDefined(em.emergencyContactLastName)
          && (this.isDefined(em.emergencyContactTelephone) || this.isDefined(em.emergencyContactWorkPhone))
        )
    }) : [];
  }

  isDefined<T>(value: T | undefined | null): value is T {
    return value !== undefined && value !== null;
  }

  getGroupedEmergencyContacts(payload: any): GroupedEmergencyContact[] {

    const contacts = this.getActiveEmergencyContacts(payload) || [];
    const grouped: Record<string, GroupedEmergencyContact> = {};

    contacts.forEach((contact: PatientEmergencyContact) => {
      const key = contact.emergencyContactFirstName + '_' + contact.emergencyContactLastName;
      if (!grouped[key]) {
        grouped[key] = {
          firstName: contact.emergencyContactFirstName,
          lastName: contact.emergencyContactLastName,
          relationship: contact.emergencyContactRelationshipValue,
          phone: contact.emergencyContactTelephone,
          types: []
        };
      }
      if (contact.emergencyContactType && contact.emergencyContactType != null && contact.emergencyContactType != '')
        grouped[key].types.push(contact.emergencyContactType);
    });

    return Object.values(grouped);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    try {
      // Split '1948-07-09' into [1948, 07, 09]
      const [year, month, day] = dateStr.split('-').map(Number);

      // Note: Month is 0-indexed in JS (January is 0)
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  calculateAge(birthDate: string): number {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }

  getCurrentModality(payload: Payload): PatientModality | null {
    if (!payload.patientModalities || payload.patientModalities.length === 0) {
      return null;
    }
    // Return the most recent modality (first in the list or the one without end date)
    return payload.patientModalities.find(m => !m.endDateTimeGmt) || payload.patientModalities[0];
  }

  getActiveFacilityDetails(payload: Payload): PatientFacility | null {
    if (!payload.patientFacilities || payload.patientFacilities.length === 0) {
      return null;
    }
    // Find active facility or return the first one
    return payload.patientFacilities.find(f => f.activeFlag === '1' || f.activeFlag === 'true')
      || payload.patientFacilities[0];
  }

  isPatientActive(payload: Payload): boolean {
    // Check multiple possible field names for active status
    // API returns 'isActive' but model has 'active'
    return (payload as any).isActive === true ||
           payload.active === true ||
           payload.status === 'active';
  }

  startNewSearch(): void {
    this.patientDetailsService.clearPatientDetailsCache();
    // Clear the search state and navigate to search
    this.searchStateService.clearSearch();
    this.router.navigate(['/search']);
  }

  // PingOne Modal
  openPingOneModal(payload: Payload): void {
    const patientName = `${payload.firstName} ${payload.familyName}`;
    const patientMpi = payload.masterPatientIdentifier;

    const dialogData: PingOneModalData = {
      pingUsers: this.pingUsers,
      patientName: patientName,
      patientMpi: patientMpi
    };

    const dialogRef = this.dialog.open(PingOneModalComponent, {
      data: dialogData,
      panelClass: 'pingone-modal-panel',
      autoFocus: false
    });

    // Handle modal close - update pingUsers if changes were made
    dialogRef.afterClosed().subscribe(result => {
      if (result?.updatedUsers) {
        this.pingUsers = result.updatedUsers;
      }
    });
  }

  getPingOneStatusClass(): string {
    if (this.pingUserLoading) return 'loading';
    if (this.pingUserError) return 'error';
    if (this.pingUsers.length === 0) return 'not-found';
    if (this.pingUsers.length > 1) return 'multiple';
    return this.pingUsers[0]?.status || 'unknown';
  }

  getPingOneStatusLabel(): string {
    if (this.pingUserLoading) return 'Loading...';
    if (this.pingUserError) return 'Error';
    if (this.pingUsers.length === 0) return 'Not Found';
    if (this.pingUsers.length > 1) return `${this.pingUsers.length} Accounts`;
    switch (this.pingUsers[0]?.status) {
      case 'active': return 'Active';
      case 'inactive': return 'Inactive';
      case 'locked': return 'Locked';
      default: return 'Linked';
    }
  }
}
